import { useState } from 'react';
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { passkeysApi } from '../../api/passkeys.js';
import { ApiError } from '../../api/client.js';
import { Card } from '../ui/Card.jsx';
import { Button } from '../ui/Button.jsx';
import { Badge } from '../ui/Badge.jsx';
import { Input } from '../ui/Input.jsx';
import { ConfirmDialog } from '../ui/ConfirmDialog.jsx';
import { Skeleton } from '../ui/Skeleton.jsx';
import { fmt } from '../../lib/formatters.js';
import { t } from '../../lib/i18n.js';

export function PasskeySection() {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState('');
  const [removeTarget, setRemoveTarget] = useState(null);

  const supported = browserSupportsWebAuthn();

  const listQuery = useQuery({
    queryKey: ['passkeys', 'list'],
    queryFn: passkeysApi.list
  });
  const items = (listQuery.data && listQuery.data.items) || [];

  const registerMutation = useMutation({
    mutationFn: async () => {
      const options = await passkeysApi.registrationOptions();
      let response;
      try {
        response = await startRegistration({ optionsJSON: options });
      } catch (err) {
        if (err && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
          throw new ApiError({ code: 'USER_ABORTED', message: 'Registrazione annullata.', statusCode: 0 });
        }
        throw new ApiError({ code: 'WEBAUTHN_FAILED', message: err.message || 'Errore WebAuthn', statusCode: 0 });
      }
      return passkeysApi.registrationVerify({ response, label: label.trim() || 'Passkey' });
    },
    onSuccess: () => {
      toast.success('Passkey registrata.');
      setLabel('');
      queryClient.invalidateQueries({ queryKey: ['passkeys', 'list'] });
    },
    onError: (err) => {
      if (err.code === 'USER_ABORTED') return;
      toast.error(err instanceof ApiError ? err.message : t('common.error'));
    }
  });

  const removeMutation = useMutation({
    mutationFn: (id) => passkeysApi.remove(id),
    onSuccess: () => {
      toast.success('Passkey rimossa.');
      queryClient.invalidateQueries({ queryKey: ['passkeys', 'list'] });
      setRemoveTarget(null);
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  return (
    <Card className="max-w-2xl">
      <header className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-ink">Passkey (WebAuthn)</h2>
          <p className="text-xs text-ink-muted">
            Accesso e step-up senza codici, con Touch ID, Face ID, Windows Hello o una chiave hardware.
          </p>
        </div>
        <Badge tone={items.length > 0 ? 'success' : 'warning'}>{items.length > 0 ? 'Configurate' : 'Nessuna'}</Badge>
      </header>

      {!supported && (
        <p className="mb-3 text-sm text-warning">
          Questo browser non supporta WebAuthn. Usa Chrome, Edge, Safari o Firefox aggiornati su HTTPS o localhost.
        </p>
      )}

      <div className="mb-4">
        {listQuery.isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-ink-muted">Nessuna passkey registrata.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {items.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm text-ink">{p.label || 'Passkey'}</p>
                  <p className="text-xs text-ink-muted">
                    Aggiunta il {fmt.date(p.createdAt)}{p.lastUsedAt ? ` · Ultimo uso ${fmt.dateTime(p.lastUsedAt)}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  className="text-xs text-danger hover:underline"
                  onClick={() => setRemoveTarget(p)}
                >
                  Rimuovi
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-border pt-4">
        <p className="mb-2 text-sm font-medium text-ink">Aggiungi una nuova passkey</p>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Input
              className="w-full"
              label="Nome (opzionale)"
              placeholder="es. iPhone, YubiKey ufficio"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <Button
            onClick={() => registerMutation.mutate()}
            loading={registerMutation.isPending}
            disabled={!supported}
          >
            Registra passkey
          </Button>
        </div>
        <p className="mt-2 text-xs text-ink-muted">
          Il browser ti chiederà di confermare con biometria o con il PIN del dispositivo.
        </p>
      </div>

      <ConfirmDialog
        open={Boolean(removeTarget)}
        title="Rimuovere la passkey?"
        description={removeTarget ? `La passkey "${removeTarget.label || 'Passkey'}" non potrà più essere usata per accedere.` : ''}
        destructive
        confirmLabel="Rimuovi"
        loading={removeMutation.isPending}
        onCancel={() => setRemoveTarget(null)}
        onConfirm={() => removeMutation.mutate(removeTarget.id)}
      />
    </Card>
  );
}
